import { useState, type KeyboardEvent } from 'react';
import { Button, Combobox, Field, Panel, PanelHeader } from '@/components/ui';
import { ExternalTextLink } from '@/components/ui/TextLink';
import { ExternalTabIcon } from '@/components/tracker-sheet/OpenTrackerSheet';
import { useBodyScrollLock } from '@/lib/use-sidebar';
import { cx } from '@/lib/cx';

/**
 * WHAT THE FOUR "Sort by" OPTIONS ASK THE SERVER FOR — owner's list, 2026-09-02.
 *
 * ⚠️ **FOUR OPTIONS, THREE SORT MEMBERS.** Company appears twice because the two
 * directions are two things a person picks between, and `order` is not a control
 * of its own here. Collapsing them into a member plus a direction toggle would be
 * two controls where the owner asked for one list.
 *
 * ⚠️ **EVERY OPTION ORDERS ROWS INSIDE THE PROJECT BANDS, NEVER ACROSS THEM.**
 * The server leads every sort with the manual project arrangement from
 * `/projects` and pins it ASC — owner's instruction the same day — so none of
 * these can move a band, and `Company name (Z–A)` reverses the rows within each
 * band rather than the bands themselves. A reader who expects the last project
 * first has not found a bug.
 *
 * ✅ **`Company name (A–Z)` IS THE WAY BACK TO THE DEFAULT, which is why a fifth
 * "Project order" option is not needed and is not missing.** The sheet is written
 * with `sort=project&order=asc` today, and because the band key leads every sort,
 * `sort=company&order=asc` produces a BYTE-IDENTICAL ordering — bands in manual
 * order, A–Z by company inside each. That is a real property of the server's
 * clause and not a coincidence of this list; `tests/client-deliverable-ordering
 * .test.ts` pins both as literal strings for exactly this reason.
 */
export const TRACKER_SHEET_SORTS = [
  {
    value: 'company:asc',
    label: 'Company name (A–Z)',
    sort: 'company',
    order: 'asc',
  },
  {
    value: 'company:desc',
    label: 'Company name (Z–A)',
    sort: 'company',
    order: 'desc',
  },
  /*
   * ⚠️ **`documentDate` ON THE WIRE, "Billing / quoted date" ON THE LABEL, AND
   * THE MISMATCH IS DELIBERATE.** A quote sorts by its issue date and an invoice
   * by its bill date — two different columns, because `d_invoices` has no issue
   * date at all — so no single column name is true of both populations. The
   * business's phrase is the right LABEL and would be a wrong KEY: `billingDate`
   * would name something that is not billed on a quote row, and the server
   * refuses that spelling by name rather than accepting a near-miss.
   *
   * ⚠️ **ASCENDING IS A CHOICE, NOT A DERIVATION.** The owner named the option
   * and not its direction. Earliest first, because the tracker's other orderings
   * read forwards and a dated list is a timeline; say so if it should be newest
   * first, because nothing here will look wrong either way.
   */
  {
    value: 'documentDate:asc',
    label: 'Billing / quoted date',
    sort: 'documentDate',
    order: 'asc',
  },
  /*
   * ⚠️ **`asc` IS THE OWNER'S ORDER — Paid, Invoiced, Quote Signed, Quote Sent,
   * Complimentary — AND IT IS NOT ALPHABETICAL, CHRONOLOGICAL OR COMMERCIAL.**
   * The ranking lives in ONE place, `BILLING_SORT_RANK` in the backend
   * repository, and nothing here may restate it: a copy in this file would be a
   * second answer to "what order do the bands read in", free to drift, and the
   * drift would be invisible because both orderings look deliberate on screen.
   */
  {
    value: 'billingStatus:asc',
    label: 'Billing status',
    sort: 'billingStatus',
    order: 'asc',
  },
] as const;

export type TrackerSheetSortValue = (typeof TRACKER_SHEET_SORTS)[number]['value'];

/**
 * The same four, in `Combobox`'s shape — owner's decision 2026-09-13, when this
 * control stopped being a `<select>`.
 *
 * ⚠️ **DERIVED, NEVER RETYPED.** A second literal list would be a second answer
 * to "what may this sheet be sorted by", free to drift from the table above —
 * and the drift is invisible, because both lists look deliberate on screen and
 * the extra member would simply never be chosen (or, worse, would be chosen and
 * `find` nothing, doing silently nothing at all).
 *
 * ⚠️ **MODULE LEVEL, NOT AN INLINE `.map()`.** `Combobox` holds `options` in a
 * `useMemo` dependency; a fresh array on every render defeats it, and this
 * component re-renders on every keystroke of a save's lifecycle. The list is
 * static, so a constant is the honest expression of that — no hook required.
 */
const TRACKER_SHEET_SORT_OPTIONS = TRACKER_SHEET_SORTS.map((option) => ({
  value: option.value,
  label: option.label,
}));

/** What the caller is handed: exactly the two query keys the list endpoint takes. */
export interface TrackerSheetSortChoice {
  sort: (typeof TRACKER_SHEET_SORTS)[number]['sort'];
  order: (typeof TRACKER_SHEET_SORTS)[number]['order'];
}

/**
 * Turns a stored `{sort, order}` back into the option that expresses it, or
 * `null` when no option does.
 *
 * ⚠️ **`null` IS A REAL ANSWER AND MUST NOT BE COALESCED TO THE FIRST OPTION.**
 * The server accepts seven sort members and this list offers four, so a setting
 * chosen through another client — or through a future longer list — is a pair
 * this control genuinely cannot show. Falling back to `Company name (A–Z)` would
 * make the dropdown state a CLAIM about the sheet that is simply false, and the
 * reader has nothing to check it against: the tab carries no record of the
 * ordering that produced it. See the caller for what it renders instead.
 */
export function trackerSheetSortValueOf(
  stored: { sort: string; order: string } | null,
): TrackerSheetSortValue | null {
  if (stored === null) return null;
  const match = TRACKER_SHEET_SORTS.find(
    (option) => option.sort === stored.sort && option.order === stored.order,
  );
  return match?.value ?? null;
}

/**
 * What a save came to, as the panel renders it.
 *
 * ⚠️ **THIS IS DELIBERATELY NOT `InlineSaveResult`**, which is the four-member
 * union the inline field editors use. That union already has TWO renderers
 * (`InlineField` and `InlineRegionsField`) and its own exhaustive `switch` in
 * each; a third would be a third file to keep in step, and the failure of
 * getting that wrong is silent — an outcome a renderer does not name reads as a
 * clean save. Three tones, one renderer, one file.
 *
 * The three are not interchangeable and each is reachable:
 *
 *   `ok`       stored, and the sheet was rewritten while we watched
 *   `warning`  **stored**, and the rewrite did not happen or could not be
 *              confirmed. ⚠️ This is a SUCCESS with a caveat, never a failure —
 *              telling somebody their choice was not saved when it was is the
 *              lie they act on, by choosing again
 *   `error`    not stored. Nothing changed
 */
export interface TrackerSheetSortOutcome {
  tone: 'ok' | 'warning' | 'error';
  message: string;
}

/**
 * Everything the sort control needs, as ONE prop.
 *
 * ⚠️ **ITS PRESENCE IS WHAT RENDERS THE CONTROL, AND `disabledReason` IS WHAT
 * DECIDES WHETHER IT WORKS.** Those are two different questions and were one
 * until 2026-09-02, when the answer to the first stopped being "only for people
 * who may change it". A caller that hid the control from non-admins would hide
 * the ORDERING ITSELF from them, and the tab has no other way of reporting it.
 */
export interface TrackerSheetSortControl {
  /**
   * The option in force, or `null` when the stored pair matches none of them —
   * see `trackerSheetSortValueOf`. `null` renders as an explicit unknown, never
   * as the first option.
   */
  value: TrackerSheetSortValue | null;
  /**
   * Why this reader may not change it, or `null` when they may.
   *
   * ⚠️ **A BARE `disabled` IS NOT ACCEPTABLE HERE**, and this prop is required
   * rather than optional so a caller cannot reach that state by omission: a
   * greyed control with no reason makes a permission denial, a derived value and
   * a broken control identical on screen.
   */
  disabledReason: string | null;
  /** Resolves once the choice has been stored, and reports what came of it. */
  onChange: (choice: TrackerSheetSortChoice) => Promise<TrackerSheetSortOutcome>;
}

/**
 * The Google Sheets mirror, embedded.
 *
 * ── 🔴 THE CALLER CHOOSES THE ADDRESS — OWNER'S INSTRUCTION, 2026-09-02 ────
 *
 * ⚠️ **BOTH PAGES NOW FRAME THE EDIT URL. THEY USED TO DIFFER.**
 *
 * SUPERSEDED: *"the Accounts Dashboard still frames its PREVIEW … **Do not
 * 'finish' the rename by switching the other caller** — everything below is the
 * cost of the edit url, and it would arrive there unasked."*
 *
 * 🔴 That reasoning was sound and the owner OVERRULED IT, after asking five
 * separate times why the two screens did not look alike. The preview url
 * renders a bare read-only grid — no Sheets icon, no document title, no menu
 * bar, no toolbar, no column letters — and the edit url renders the Sheets
 * application. **That single difference is what he was pointing at every time,
 * and it was defended as a decision rather than compared against his
 * screenshots.** His spreadsheet, his call.
 *
 * The costs listed below are real and are now ACCEPTED on both documents, not
 * avoided on one. Do not restore the preview url on either caller thinking you
 * are fixing a regression.
 *
 * *SUPERSEDED, and it was the load-bearing decision in this file: "**IT EMBEDS
 * `/preview`, AND NEVER `/edit`** … an editable embed would offer a cursor over
 * a document nobody's work survives in … The read-only form makes the one-way
 * nature of the mirror visible instead of hiding it behind a grid that appears
 * to accept edits."* The owner asked for an embed people can edit and comment
 * in where Google says they may. **Google decides that, not this app**: the
 * frame carries the viewer's own Google session, so a commenter comments, an
 * editor edits, and somebody with view access still only reads.
 *
 * ⚠️ **THE COST THAT PARAGRAPH NAMED IS REAL AND IS NOW ACCEPTED, not
 * disproved** — and it is halved rather than removed, which is the part worth
 * keeping straight:
 *
 * - **Comments SURVIVE.** They anchor to cells and a values write does not touch
 *   them, which is the durable half — and the half the workbook this replaces
 *   actually ran on: 97 of its 117 threaded comments were one person asking
 *   another to look a billing fact up.
 * - **Cell edits inside the written rectangle are REFUSED BY GOOGLE**, visibly,
 *   because the mirror protects that range. A visible refusal is the good
 *   failure; it is why the protection is not being removed to "finish" this.
 * - ⚠️ **Anything typed OUTSIDE the protected rectangle is erased on the next
 *   tick** — no warning, no conflict, nothing to recover from. That is the one
 *   silent loss left, and it is why the header below still says the sheet is
 *   rewritten rather than presenting a grid that merely looks editable.
 *
 * ⚠️ The URL comes from the SERVER — `spreadsheetUrl`, with the preview as the
 * fallback for an older API. Do not derive one from the other here by swapping
 * `/edit` for `/preview`: the shape of a Google address is a fact the API owns
 * — see `ClientDeliverableSheetLink` — and a bad swap does not error, it renders
 * an empty frame that looks like a permissions problem.
 *
 * ⚠️ **UNMEASURED, and stated rather than guessed: whether the edit URL frames
 * as reliably as the preview one did.** What IS measured (2026-08-30) is that
 * `docs.google.com` sends no `X-Frame-Options` and no `frame-ancestors` on
 * `/edit`, `/preview` or `/pubhtml` — so Google does not block framing by
 * header. What is left is third-party cookie behaviour, which only a real
 * browser holding a real Google session can answer, and no browser here holds
 * one. The fallback line below is what makes that survivable either way.
 *
 * ── NO `sandbox`, DELIBERATELY AND ON THE EVIDENCE ────────────────────────
 *
 * ⚠️ A `sandbox` attribute with the wrong token set does not warn, log or throw
 * — the frame simply paints nothing, which is indistinguishable here from a
 * viewer who is not signed into Google. The house rule for this app is that a
 * control is only shipped in a state somebody has actually driven, and the
 * sandbox tokens Google Sheets needs **have not been driven against a real
 * signed-in session**, because the browsers this was tested in hold no Google
 * account. So it is omitted rather than guessed: an omitted attribute is the
 * documented default, where a guessed one is an untested claim that fails blank.
 * Add one only alongside a screenshot of the sheet still rendering with it.
 *
 * ── WHY THE FALLBACK LINE IS NOT HELPER TEXT ──────────────────────────────
 *
 * ⚠️ **A cross-origin iframe that fails to load reports NOTHING.** There is no
 * usable `onError`, no readable status, and no way to ask the frame what
 * happened — so this component cannot detect a blank frame and cannot render a
 * different state when one occurs. The line below the frame is therefore
 * unconditional, and it is the only thing standing between "Google did not
 * paint" and a dead end. It survives the no-helper-text prohibition on the same
 * ground `EmptyState`'s description does: it is a route out of a state the
 * reader can otherwise do nothing about, not an explanation of a control.
 *
 * ── HEIGHT, AND THE FULL-SCREEN MODE ──────────────────────────────────────
 *
 * `70vh` with a floor, rather than filling the viewport. A frame as tall as the
 * screen is a scroll trap: the wheel belongs to the spreadsheet everywhere the
 * pointer can be, so the page underneath cannot be reached. Leaving the header
 * above and the fallback line below outside the frame keeps a real surface to
 * scroll the page by at every width, including 320.
 *
 * ✅ **FULL SCREEN IS THE OPT-IN THAT ANSWERS THAT — owner's request,
 * 2026-09-02.** The scroll-trap objection was always about the DEFAULT: a person
 * who presses a button asking for the whole window has asked for the trap, and
 * has a way out of it. The page scroll is locked while it is open, so there is
 * no page underneath to fail to reach.
 *
 * ⚠️ **IT IS THE SAME ELEMENT, POSITIONED — NOT A PORTAL, AND NOT A SECOND
 * FRAME.** Rendering an expanded copy elsewhere in the tree unmounts this
 * `<iframe>` and mounts another, and **a remounted frame RELOADS the
 * spreadsheet**: scroll position, the cell somebody had selected, a
 * half-typed comment. Toggling twice would cost two reloads. Changing this
 * component's own classes keeps one frame alive for the life of the panel, so
 * entering and leaving costs nothing at all.
 *
 * ⚠️ **THAT MAKES THE LAYER LADDER LOAD-BEARING, and the ladder's own warning
 * applies exactly here.** `z-50` is the overlays rung, above app chrome at 30 —
 * but a number inside a stacking context is LOCAL, so this only clears the
 * sticky top bar because nothing between `<main>` and this panel creates one
 * (`<main>` carries `mx-auto w-full max-w-7xl flex-1 px-4 py-6` — no transform,
 * no opacity, no z-index). **If a wrapper ever gains one, this panel will paint
 * UNDER the top bar with the right z-index and nothing will error.** Measured in
 * a browser at 320/375/768/1440 rather than reasoned about; re-measure if the
 * shell changes.
 *
 * ⚠️ **ESCAPE CANNOT BE THE WAY OUT, AND THIS IS THE ONE THING NOT TO
 * "SIMPLIFY".** The moment somebody clicks into the sheet, focus is inside a
 * CROSS-ORIGIN iframe: Google swallows every keystroke and this document never
 * sees them, so no `keydown` handler here — on the panel, on `document`, or
 * anywhere else — can fire. Escape works only while focus is still outside the
 * frame, which is why it is a convenience and **the always-visible Exit button
 * is the guarantee.** Never move that control behind a hover, a menu, or the
 * frame itself.
 *
 * Escape is handled on the panel rather than on `document` for the same reason
 * the ladder prefers a local answer: the keystroke reaches here by bubbling from
 * the Exit button, which is where focus already is after the toggle — no ref, no
 * global listener, and nothing to remove on unmount.
 */
export function TrackerSheetPanel({
  embedUrl,
  openUrl,
  frameTitle = 'Client Deliverables tracker in Google Sheets',
  sort,
}: {
  /**
   * What the frame is announced as. Defaulted rather than required so the
   * existing caller keeps the exact string it had — a required member here
   * would be the right instrument if this were a value a caller must supply,
   * but both real answers already exist and only one caller was wrong.
   */
  frameTitle?: string;
  /**
   * What goes in the frame: the EDIT address, or the preview against a server
   * that sends no edit URL. The caller decides which — see the page.
   */
  embedUrl: string;
  /**
   * Where the fallback line points. `null` only against a server that sent a
   * preview URL and no edit URL, which the API's single builder cannot produce
   * — the fallback then points at whatever is framed, which is still a real
   * document.
   */
  openUrl: string | null;
  /**
   * ✅ **THE SORT CONTROL — WIRED 2026-09-02, AND THE THREE PRECONDITIONS THAT
   * HELD IT BACK ARE ALL MET.**
   *
   * *SUPERSEDED, and it was the load-bearing paragraph in this file: "**THE SORT
   * CONTROL RENDERS ONLY WHEN THIS IS PASSED, AND NOTHING PASSES IT YET. THAT IS
   * THE FEATURE, NOT AN UNFINISHED EDGE** … the mirror worker … asks the API for
   * `sort: 'project', order: 'asc'` — hardcoded … **So a dropdown here today
   * could change nothing at all** … **ABSENT RATHER THAN DISABLED, ON THIS
   * REPO'S OWN PRECEDENT** … this refusal is about the system, is identical for
   * everybody, and ends with a deploy rather than with a permission."*
   *
   * Every clause of that was true and every one has been closed:
   * `client-deliverables-sheet.service.ts` reads a stored ordering (migration
   * 143), `ClientDeliverableSort` names all seven members, and the mount decides
   * what a non-admin sees.
   *
   * 🔴 **THE ABSENT-RATHER-THAN-DISABLED RULING IS REVERSED, AND THE REVERSAL IS
   * THE WHOLE POINT OF READING THIS.** It was correct on its own reasoning: the
   * refusal was about the SYSTEM, identical for everybody, and a control whose
   * only outcome is nothing is worse than one whose outcome is an error. **The
   * refusal that exists now is a different refusal.** The write is admin-only and
   * the read is open, so what a non-admin meets is a limit on THIS READER — which
   * is the DocuSign `viewer` shape, where a bare `disabled` makes a permission
   * denial and a broken control identical and the reason has to be visible.
   *
   * ⚠️ **AND HIDING IT WOULD HIDE THE ORDERING ITSELF.** A tab carries no record
   * of the arrangement that produced it, so a reader who cannot see this control
   * has no way at all to learn how the sheet in front of them is sorted. That is
   * the second reason, and it is independent of the first.
   *
   * ⚠️ **THE COPY WARNING SURVIVES ITS OWN ARGUMENT.** That paragraph reasoned
   * that gating the control made its copy safe by construction. It is no longer
   * gated, so nothing is safe by construction: every sentence beside it is now an
   * ordinary claim about server behaviour that has to be true today. The one that
   * matters is that **the choice changes the document for every reader** — see
   * the shared-state line below, which is a consequence nobody can see from a
   * dropdown and is therefore not helper text.
   */
  sort?: TrackerSheetSortControl;
}) {
  const [expanded, setExpanded] = useState(false);
  /**
   * The last save's outcome, or `null` when nothing has been saved this mount.
   *
   * ⚠️ **IT IS NOT DISMISSED ON A TIMER.** A message that vanishes after N
   * seconds is one a reader can miss entirely — and the two messages worth
   * reading here are the `warning` (stored, but the sheet has not moved yet) and
   * the `error`, both of which the reader may need while they go and do something
   * about it. It is replaced by the next save and by nothing else.
   */
  const [outcome, setOutcome] = useState<TrackerSheetSortOutcome | null>(null);
  /**
   * ⚠️ **THE CONTROL IS DISABLED WHILE A SAVE IS IN FLIGHT, and that is not
   * cosmetic.** Two overlapping `PUT`s against one shared row would land in
   * whatever order the network chose, so the last one to ARRIVE would win rather
   * than the last one CHOSEN — and the dropdown would sit showing the other one.
   */
  const [saving, setSaving] = useState(false);
  /*
   * ⚠️ **THE PAGE BEHIND MUST NOT SCROLL.** Without this the wheel over the
   * panel's own chrome scrolls a page nobody can see, and leaving full screen
   * lands somewhere other than where it was entered from. It is also what
   * retires the scroll-trap objection this component's header records: there is
   * no page underneath to fail to reach.
   */
  useBodyScrollLock(expanded);

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    // See the header: this fires only while focus is OUTSIDE the frame. It is
    // the convenience; the Exit button is the guarantee.
    if (expanded && event.key === 'Escape') {
      event.stopPropagation();
      setExpanded(false);
    }
  }

  return (
    <Panel
      onKeyDown={onKeyDown}
      /*
       * `role`/`aria-modal` only while expanded: a panel sitting in the page is
       * a section, and claiming it is a dialog would hide the rest of the page
       * from a screen reader for a surface nobody has opened.
       *
       * ⚠️ `aria-modal` here is a claim about the REST OF THE PAGE, not a focus
       * trap — one cannot be built across a cross-origin frame. `ActivityDrawer`
       * carries the same limitation for the same reason.
       */
      {...(expanded
        ? { role: 'dialog' as const, 'aria-modal': true, 'aria-label': 'Google Sheets mirror' }
        : {})}
      className={cx(
        expanded &&
          // Full bleed: the rounding and the border are what make a panel read
          // as a card in a page, and neither belongs on something that IS the
          // page. `flex-col` + `min-h-0` below is what lets the frame take the
          // height the header and the fallback line do not.
          'fixed inset-0 z-50 flex flex-col rounded-none border-0 shadow-none',
      )}
    >
      {/*
       * ⚠️ **"Read-only." IS GONE AND THE REST OF THE SENTENCE IS NOT.** The
       * frame is the edit URL now, so that word was about to become the reverse
       * of what the grid does — but the rewrite it sat beside is the wrong
       * conclusion a reader can still draw, and it is now MORE dangerous, not
       * less: a cursor that accepts text is an invitation. The description
       * states the consequence and points at where an edit belongs.
       *
       * No cadence is quoted: the mirror's interval is a FLOOR that stretches
       * on failure, so a number here would be wrong precisely when it mattered.
       *
       * ⚠️ It does not promise editing either. Whether this reader may type is
       * Google's answer, given per person, and this app cannot see it — a
       * description claiming "you can edit this" would be wrong for everybody
       * with view access.
       */}
      <PanelHeader
        title="Google Sheets mirror"
        description="EMERGE rewrites this sheet automatically — edit the record in EMERGE, not here."
        /*
         * ⚠️ **ONE control that toggles, not two.** A separate Exit somewhere
         * else would be a second place to look for the way out of a state whose
         * whole hazard is that the way out has to be obvious. It also keeps
         * focus on the same element across the toggle, which is what lets
         * Escape bubble here without a ref or a global listener.
         *
         * ⚠️ **`shrink-0` in `PanelHeader`'s actions slot means this cannot
         * wrap**, so the label stays short. At 320 the header stacks the title
         * above the actions, which is the slot's own behaviour and is why a
         * long label would push the button off the edge rather than wrapping.
         */
        actions={
          <>
            {/*
             * ⚠️ **LEFT OF Full screen — owner's placement — AND IT IS RENDERED
             * ONLY WHEN A HANDLER EXISTS.** See `onSortChange`: without one the
             * choice reaches nothing, and a dropdown that changes nothing is the
             * defect this app has shipped most often.
             */}
            {sort && (
              /*
               * ⚠️ **THE WRAPPER IS WHAT SIZES IT, NOT a `w-` ON THE CONTROL.**
               * *SUPERSEDED 2026-09-13, the mechanism only: "`Select` puts
               * `className` on the `<select>` itself, which is already `w-full`
               * inside its own relative wrapper — so a width passed in would
               * fight the primitive."* `Combobox` takes **no `className` at
               * all** — its trigger is a fixed `w-full min-w-0` button — so a
               * wrapper is now the ONLY way to size this, rather than merely the
               * right one. The conclusion did not move; the reason did.
               *
               * ⚠️ **`flex-1` BELOW `sm` AND A FIXED WIDTH ABOVE IT.**
               * `PanelHeader` stacks below `sm` and the actions row stretches to
               * the full width there, so the control takes what the Full screen
               * button leaves — which is what keeps both on one line at 320,
               * where the slot is `shrink-0` and cannot wrap. Above `sm` the row
               * sizes to its content, so a fixed width is what stops the longest
               * label setting the header's width.
               *
               * 🔴 **`sm:w-44` IS DELIBERATELY UNCHANGED ACROSS THE REWRITE —
               * 2026-09-13.** The slot cannot wrap, so a wider control here is
               * paid for in header layout at every width; keeping the footprint
               * identical is what makes this change a control swap and not a
               * layout change. Two things make that safe rather than merely
               * conservative, and both run the other way from a `<select>`:
               *
               * - the trigger's label span is `min-w-0 truncate`, so its
               *   min-content is a chevron and some padding. A native `<select>`
               *   is floored by its LONGEST OPTION in every engine — so the
               *   narrow end of this control got narrower, not wider;
               * - the popover is `width: max-content`, floored at 240px and
               *   portalled to the body, so the LIST is legible whatever the
               *   trigger is. The trigger no longer has to be wide enough to
               *   hold `Company name (A–Z)`, which is the one thing that would
               *   have argued for widening it.
               *
               * **Measure before widening it**; do not widen it because the
               * truncated trigger reads short beside the full option list.
               */
              <div className="min-w-0 flex-1 sm:w-44 sm:flex-none">
                {/*
                  * ✅ **A SEARCHABLE `Combobox` — owner's decision 2026-09-13,
                  * every non-column filter dropdown in the app takes this
                  * shape.** *SUPERSEDED: a `ui/Select` with an inline `<option>`
                  * list and a conditional placeholder row.* Four options do not
                  * need a search box on their own account; the owner chose one
                  * shape over a per-control judgement, and this is the control
                  * that was hardest to move rather than an exception to it.
                  *
                  * ⚠️ **NO VISIBLE LABEL AND NO HINT**, unchanged: the label
                  * would cost the width that keeps this on one line at 320, and
                  * helper text is prohibited outright. "Sort by" is the owner's
                  * own word for the control and claims nothing about what it
                  * sorts — the option labels say that.
                  *
                  * ⚠️ **`aria-label` BECAME AN `sr-only` `Field` LABEL, AND THAT
                  * IS A MECHANISM CHANGE, NOT A WORDING ONE.** `Combobox`
                  * declares no `aria-label` — passing one is a type error, not a
                  * silently dropped prop — but its trigger is a `<button
                  * id={id}>`, which IS a labelable element, so `Field`'s hidden
                  * `<label htmlFor>` names it with the same three words. This is
                  * the house pattern every converted filter uses. **Do not
                  * "simplify" it back to an attribute**: the wrapper is what
                  * carries the accessible name now, and deleting it leaves a
                  * button announced as its own current value.
                  *
                  * ⚠️ **FULL SCREEN IS THE ONE PLACE A `<select>` HAD AN EDGE,
                  * AND THE LAYER LADDER IS WHAT CLOSES IT.** A native dropdown
                  * is browser chrome and is subject to nothing on the page; this
                  * popover is markup. It survives because it is PORTALLED to the
                  * body on rung 60, so it escapes both the clipping and the
                  * stacking context of a panel that is `fixed inset-0 z-50` — the
                  * same reason a `Select` inside a `Modal` opens over it. Give a
                  * new layer its own rung; never reuse a neighbour's.
                  *
                  * ⚠️ **Accepted, and stated rather than discovered: in full
                  * screen the panel claims `aria-modal`, and the popover is
                  * portalled OUTSIDE it.** Assistive tech may therefore treat the
                  * list as outside the modal. That is a property this app
                  * already carries everywhere a portalled popover opens inside a
                  * dialog, `Modal` included, and the panel's own header records
                  * that its `aria-modal` is a claim about the rest of the page
                  * rather than a focus trap — one cannot be built across a
                  * cross-origin frame. **Fix it in the primitive if it is ever
                  * fixed**, not here.
                  */}
                <Field label="Sort by" hideLabel>
                  {({ id }) => (
                    <Combobox
                      id={id}
                      /*
                       * ⚠️ **DISABLED FOR A NON-ADMIN AND WHILE SAVING, AND THE
                       * REASON IS RENDERED AS TEXT BELOW** — never only as a
                       * `title`, which a keyboard or touch reader never sees.
                       *
                       * ⛔ **NOT `loading`, and the difference is the whole
                       * point of the prop.** The `/deals` bank turned its
                       * `disabled` into `loading` on the same day, because there
                       * it meant "the master list is on its way" and the control
                       * should stay operable. Here it means **"you may not
                       * change this"** and **"a save is in flight"** — two real
                       * refusals, one of which the server would make itself
                       * (`PUT` is `ADMIN_ROLES`). A `loading` state would leave
                       * the list pickable and race the save that is running.
                       * Do not carry that rename across.
                       */
                      disabled={sort.disabledReason !== null || saving}
                      /*
                       * ⚠️ **`''` WHEN THE STORED PAIR MATCHES NO OPTION.** The
                       * server offers seven sort members and this list four, so
                       * that state is reachable today — and showing `Company
                       * name (A–Z)` for a sheet ordered some other way would be
                       * a false claim the reader cannot check: the tab carries
                       * no record of the ordering that produced it.
                       */
                      value={sort.value ?? ''}
                      onChange={(value) => {
                        /*
                         * ⚠️ **LOOKED UP, NEVER SPLIT ON THE ':'.** Parsing the
                         * value back into a pair would be a second,
                         * string-shaped copy of the table above, and it would
                         * happily manufacture a `{sort, order}` the server
                         * refuses — an unrecognised `sort` is a 422 naming the
                         * key. The `find` cannot invent one.
                         *
                         * ⚠️ **THIS GUARD IS ALSO WHAT MAKES THE PINNED ROW
                         * INERT**, and it is load-bearing rather than defensive
                         * now. `emptyOptionLabel` renders a row somebody can
                         * genuinely select, where the `<select>`'s placeholder
                         * `<option>` was merely present — so `''` arrives here
                         * on a real click, finds nothing, and does nothing.
                         * Exactly what re-picking the old placeholder did.
                         */
                        const chosen = TRACKER_SHEET_SORTS.find(
                          (option) => option.value === value,
                        );
                        if (!chosen) return;

                        setSaving(true);
                        // Cleared FIRST, so the previous save's sentence cannot
                        // sit beside a control that has since moved on — a stale
                        // "Saved." over a choice that is still in flight reads as
                        // confirmation of the wrong thing.
                        setOutcome(null);
                        void sort
                          .onChange({ sort: chosen.sort, order: chosen.order })
                          .then(setOutcome)
                          .finally(() => setSaving(false));
                      }}
                      options={TRACKER_SHEET_SORT_OPTIONS}
                      /*
                       * 🔴 **THE "Sorted some other way" ROW IS PINNED
                       * CONDITIONALLY, AND BOTH HALVES OF THIS ARE REQUIRED.**
                       *
                       * **`emptyOptionLabel` — RENDERED ONLY WHEN IT IS THE
                       * CURRENT STATE**, which is the rule the `<select>`'s
                       * conditional `<option>` carried and the one thing about
                       * this control that must not be lost in translation. A
                       * permanently pinned row offers "unknown" as something to
                       * pick, and there is nothing to send for it.
                       *
                       * **`placeholder` — the SAME SENTENCE, and it is what the
                       * reader actually SEES.** `Combobox` resolves the
                       * trigger's label out of `options` ALONE, so the row it
                       * synthesises for `''` is never `selected`: the trigger
                       * falls back to the placeholder. Pass the pin without the
                       * placeholder and a sheet sorted some other way reads
                       * `Select…`; pass neither and it reads `Company name
                       * (A–Z)` — the false claim above, restored by omission.
                       *
                       * They therefore say the same words on purpose, and the
                       * duplication is a guard: the control cannot name a sort
                       * it does not have even if one of the two is dropped.
                       *
                       * ⛔ **THE LIST STAYS AT FOUR.** The server accepts seven
                       * members; widening what is OFFERED is a decision about
                       * the feature, not about the control — see
                       * `TRACKER_SHEET_SORTS`.
                       */
                      {...(sort.value === null
                        ? { emptyOptionLabel: 'Sorted some other way' }
                        : {})}
                      placeholder="Sorted some other way"
                      searchPlaceholder="Search sort options…"
                    />
                  )}
                </Field>
              </div>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setExpanded((current) => !current)}
              aria-expanded={expanded}
            >
              {expanded ? 'Exit full screen' : 'Full screen'}
            </Button>
          </>
        }
      />

      {/*
       * ⚠️ **TWO LINES, AND THEY ARE NOT COALESCED WITH A `??`.** The standing
       * line is a FACT about the control (who this changes it for, or why this
       * reader cannot); the outcome line is an EVENT (what the last save came
       * to). Both can be true at once, and this codebase's `a ?? b` rule is
       * exactly that: where both sides can be true, rendering one is a silent
       * drop. They stack, shortest first.
       *
       * ⚠️ **NEITHER IS HELPER TEXT UNDER THE STANDING PROHIBITION**, and the
       * test is what wrong conclusion each prevents:
       *
       *   the shared line   that this is a view preference. It is one document
       *                     shared with everybody it is shared with, so changing
       *                     it changes what every other reader sees — a
       *                     consequence a dropdown conveys nothing about
       *   the denial        that the control is broken. A bare greyed `<select>`
       *                     makes a permission denial and a fault identical
       *   the outcome       that the sheet has already moved, or that a stored
       *                     choice was lost. Both are refusals or reports of one
       *
       * ⚠️ **NO INTERVAL IS QUOTED.** The mirror's tick is a FLOOR that stretches
       * on failure, and the number lives in one constant in the backend worker —
       * prose here would be wrong precisely when it mattered, and this codebase
       * has already had "five minutes" spread through a module whose worker says
       * ten. "The next sync" is true whatever the constant says.
       */}
      {sort && (
        /*
         * ⚠️ `shrink-0`, for the reason the fallback line below has it: in full
         * screen the panel is a flex column and the frame is `flex-1`, so a band
         * that can shrink gets squeezed to nothing by it — and the sentence that
         * disappears would be the denial or the failure report.
         */
        <div className="shrink-0 space-y-1 border-b border-border px-4 py-2">
          {outcome && (
            <p
              /*
               * `role="status"` rather than `alert`, including for the error:
               * this reports on something the reader has just done and is
               * looking at, so an assertive interruption is louder than the
               * event. Announced politely, once, on change.
               */
              role="status"
              className={cx(
                'm-0 text-xs',
                outcome.tone === 'ok' && 'text-success',
                outcome.tone === 'warning' && 'text-warning',
                outcome.tone === 'error' && 'text-danger',
              )}
            >
              {outcome.message}
            </p>
          )}
          {/*
           * ⚠️ **THE REFUSAL ONLY — owner's ruling, 2026-09-05.** This used to
           * fall back to "Sorting changes this sheet for everyone." for a reader
           * who CAN sort; that was a consequence note rather than a refusal and
           * went with the rest of this app's helper text. `disabledReason` stays
           * because it says why the control is greyed for THIS reader, which is
           * a permission denial the server would really make.
           */}
          {sort.disabledReason !== undefined && sort.disabledReason !== null && (
            <p className="m-0 text-xs text-fg-muted">{sort.disabledReason}</p>
          )}
        </div>
      )}

      <div
        className={cx(
          'border-b border-border',
          // `min-h-0` is what makes `flex-1` actually shrink here: a flex item
          // defaults to `min-height: auto`, so without it the frame's own height
          // wins and the panel overflows the viewport it is pinned to.
          expanded && 'flex min-h-0 flex-1',
        )}
      >
        <iframe
          src={embedUrl}
          /*
           * ⚠️ No longer "(read-only)". A frame title is read aloud before
           * anybody reaches the grid, so it was the first thing to state a
           * property the frame no longer has.
           */
          /*
           * ⚠️ Named by the CALLER, because two different documents mount this
           * and a hardcoded title announced the Accounts Dashboard's sheet to a
           * screen reader as the Client Deliverables tracker. It is the only
           * thing a non-sighted reader is told about the frame, and the frame
           * is cross-origin, so nothing else here can describe its contents.
           */
          title={frameTitle}
          /*
           * ⚠️ `lazy` and NOT `eager`, even though the frame is the default
           * view. It stays on the toggle too: the element is never remounted,
           * so this attribute is read once for the life of the panel and full
           * screen cannot re-trigger a load.
           */
          loading="lazy"
          className={cx(
            'block w-full border-0 bg-surface',
            // The floor is what stops a short viewport collapsing the frame to
            // nothing; in full screen there is no floor, because the frame is
            // whatever is left after the header and the fallback line.
            expanded ? 'h-full min-h-0 flex-1' : 'h-[70vh] min-h-[360px]',
          )}
        />
      </div>

      {/*
       * ⚠️ **KEPT IN FULL SCREEN, and that is not an oversight to tidy.** A
       * cross-origin frame that fails to paint reports nothing, so this line is
       * the only route out of a blank rectangle — and a blank rectangle filling
       * the whole window is exactly where somebody needs it most.
       */}
      <p className="m-0 shrink-0 px-4 py-3 text-xs">
        {/*
         * ⚠️ **THE LINK STAYS AND THE SENTENCE IS GONE — owner's ruling,
         * 2026-09-05.** "Not showing above?" was a caption; the anchor is a
         * control, and it is still the only route out of a frame that failed to
         * paint, which reports nothing. Do not delete the link with the prose.
         */}
        <ExternalTextLink
          href={openUrl ?? embedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-medium"
        >
          Open the sheet in Google Sheets
          <ExternalTabIcon className="h-3 w-3" />
          <span className="sr-only"> (opens in a new tab)</span>
        </ExternalTextLink>
      </p>
    </Panel>
  );
}
